import React, { useState, useEffect, useCallback } from "react";
import { ethers } from "ethers";
import { useAuth } from "../contexts/AppKitAuthContext";
import { useScrollLock } from "../hooks/useScrollLock";
import { useAlert } from "../hooks/useAlert";
import TopNavigation from "../components/TopNavigation";
import AlertPopup from "../components/AlertPopup";
import { appKitBlockchainService } from "../services/appkitBlockchain";
import styles from "../styles/InvestPage.module.css";

interface AgreementDisplay {
  agreementId: number;
  investor: string;
  investTokenAddr: string;
  investTokenAmount: bigint;
  dxfTokenAmount: bigint;
  expiresAt: bigint;
  status: number;
  isExpired: boolean;
  investTokenSymbol: string;
  investAmountFormatted: string;
  dxfAmountFormatted: string;
  expiresAtDate: Date;
  decimals: number;
  balance?: string;
  allowance?: string;
}

const STATUS_LABELS: Record<number, { label: string; className: string }> = {
  0: { label: "Not Configured", className: styles.statusNotConfigured },
  1: { label: "Ready", className: styles.statusReady },
  2: { label: "Completed", className: styles.statusCompleted },
  3: { label: "Canceled", className: styles.statusCanceled },
};

const InvestPage: React.FC = () => {
  const { isAuthenticated, walletAddress, walletState, updateBalance } = useAuth();
  const { alert, showError, showSuccess } = useAlert();
  const [agreements, setAgreements] = useState<AgreementDisplay[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [processingAgreementId, setProcessingAgreementId] = useState<number | null>(null);
  const [approvingAgreementId, setApprovingAgreementId] = useState<number | null>(null);

  useScrollLock(false);

  const fetchAgreements = useCallback(async () => {
    if (!walletAddress) return;

    setIsLoading(true);
    try {
      // Get agreement IDs for this investor
      const agreementIds = await appKitBlockchainService.getAgreementsByInvestor(walletAddress);

      if (agreementIds.length === 0) {
        setAgreements([]);
        setIsLoading(false);
        return;
      }

      // Fetch details for each agreement
      const agreementPromises = agreementIds.map(async (id) => {
        const agreement = await appKitBlockchainService.getAgreement(id);
        const now = Math.floor(Date.now() / 1000);
        const isExpired = Number(agreement.expiresAt) < now;

        // Get token symbol and decimals
        let investTokenSymbol = "BNB";
        let decimals = 18;
        if (agreement.investTokenAddr !== ethers.ZeroAddress) {
          investTokenSymbol = await appKitBlockchainService.getERC20Symbol(agreement.investTokenAddr);
          decimals = await appKitBlockchainService.getERC20Decimals(agreement.investTokenAddr);
        }

        // Format amounts
        const investAmountFormatted = ethers.formatUnits(agreement.investTokenAmount, decimals);
        const dxfAmountFormatted = ethers.formatUnits(agreement.dxfTokenAmount, 18);

        // Get balance and allowance for ERC20 tokens if status is READY
        let balance: string | undefined;
        let allowance: string | undefined;
        if (agreement.status === 1 && agreement.investTokenAddr !== ethers.ZeroAddress) {
          balance = await appKitBlockchainService.getERC20Balance(
            agreement.investTokenAddr,
            walletAddress
          );
          allowance = await appKitBlockchainService.getERC20AllowanceForFundRaising(
            agreement.investTokenAddr,
            walletAddress
          );
        }

        return {
          agreementId: Number(id),
          ...agreement,
          isExpired,
          investTokenSymbol,
          investAmountFormatted,
          dxfAmountFormatted,
          expiresAtDate: new Date(Number(agreement.expiresAt) * 1000),
          decimals,
          balance,
          allowance,
        };
      });

      let allAgreements = await Promise.all(agreementPromises);

      // Sort: READY first, then others
      allAgreements = allAgreements.sort((a, b) => {
        if (a.status === 1 && b.status !== 1) return -1;
        if (a.status !== 1 && b.status === 1) return 1;
        return b.agreementId - a.agreementId; // Newer first within same status
      });

      setAgreements(allAgreements);
    } catch (error) {
      console.error("Failed to fetch agreements:", error);
      showError("Data Fetch Failed", "Could not load investment agreements");
    } finally {
      setIsLoading(false);
    }
  }, [walletAddress, showError]);

  useEffect(() => {
    if (isAuthenticated && walletAddress) {
      fetchAgreements();
    }
  }, [isAuthenticated, walletAddress, fetchAgreements]);

  const handleInvestBNB = async (agreement: AgreementDisplay) => {
    if (!walletAddress) return;

    // Check BNB balance
    const bnbBalance = ethers.parseEther(walletState.balance || "0");
    if (bnbBalance < agreement.investTokenAmount) {
      showError(
        "Insufficient Balance",
        `You need ${agreement.investAmountFormatted} BNB but only have ${walletState.balance} BNB`
      );
      return;
    }

    setProcessingAgreementId(agreement.agreementId);
    try {
      await appKitBlockchainService.investInAgreement(
        BigInt(agreement.agreementId),
        agreement.investTokenAmount
      );

      showSuccess(
        "Investment Successful!",
        `You invested ${agreement.investAmountFormatted} BNB and received ${agreement.dxfAmountFormatted} DXF tokens`
      );

      // Wait a bit for blockchain confirmation
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Refresh agreements and balances
      await fetchAgreements();
      await updateBalance();
    } catch (error: any) {
      console.error("Investment failed:", error);
      if (error.message?.includes("user rejected")) {
        showError("Transaction Rejected", "You rejected the transaction");
      } else {
        showError("Investment Failed", error.message || "Unknown error occurred");
      }
    } finally {
      setProcessingAgreementId(null);
    }
  };

  const handleApproveERC20 = async (agreement: AgreementDisplay) => {
    if (!walletAddress) return;

    setApprovingAgreementId(agreement.agreementId);
    try {
      // Approve max amount to avoid repeated approvals
      const maxApproval = ethers.MaxUint256.toString();
      const maxApprovalFormatted = ethers.formatUnits(maxApproval, agreement.decimals);

      await appKitBlockchainService.approveERC20ForFundRaising(
        agreement.investTokenAddr,
        maxApprovalFormatted,
        agreement.decimals
      );

      showSuccess("Approval Successful", "You can now proceed with the investment");

      // Wait a bit for blockchain confirmation
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Refresh agreements to update allowance
      await fetchAgreements();
    } catch (error: any) {
      console.error("Approval failed:", error);
      if (error.message?.includes("user rejected")) {
        showError("Transaction Rejected", "You rejected the approval");
      } else {
        showError("Approval Failed", error.message || "Unknown error occurred");
      }
    } finally {
      setApprovingAgreementId(null);
    }
  };

  const handleInvestERC20 = async (agreement: AgreementDisplay) => {
    if (!walletAddress) return;

    // Check token balance
    const balanceBigInt = ethers.parseUnits(agreement.balance || "0", agreement.decimals);
    if (balanceBigInt < agreement.investTokenAmount) {
      showError(
        "Insufficient Balance",
        `You need ${agreement.investAmountFormatted} ${agreement.investTokenSymbol} but only have ${agreement.balance} ${agreement.investTokenSymbol}`
      );
      return;
    }

    // Check allowance
    const allowanceBigInt = ethers.parseUnits(agreement.allowance || "0", agreement.decimals);
    if (allowanceBigInt < agreement.investTokenAmount) {
      showError(
        "Approval Required",
        `Please approve ${agreement.investTokenSymbol} tokens first`
      );
      return;
    }

    setProcessingAgreementId(agreement.agreementId);
    try {
      await appKitBlockchainService.investInAgreement(
        BigInt(agreement.agreementId)
      );

      showSuccess(
        "Investment Successful!",
        `You invested ${agreement.investAmountFormatted} ${agreement.investTokenSymbol} and received ${agreement.dxfAmountFormatted} DXF tokens`
      );

      // Wait a bit for blockchain confirmation
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Refresh agreements and balances
      await fetchAgreements();
      await updateBalance();
    } catch (error: any) {
      console.error("Investment failed:", error);
      if (error.message?.includes("user rejected")) {
        showError("Transaction Rejected", "You rejected the transaction");
      } else {
        showError("Investment Failed", error.message || "Unknown error occurred");
      }
    } finally {
      setProcessingAgreementId(null);
    }
  };

  const renderAgreementActions = (agreement: AgreementDisplay) => {
    if (agreement.status !== 1) {
      // Not READY status
      const statusInfo = STATUS_LABELS[agreement.status];
      return (
        <div className={styles.statusOnlyBadge}>
          <span className={statusInfo.className}>{statusInfo.label}</span>
        </div>
      );
    }

    if (agreement.isExpired) {
      return (
        <div className={styles.statusOnlyBadge}>
          <span className={styles.statusExpired}>Expired</span>
        </div>
      );
    }

    // READY and not expired
    const isProcessing = processingAgreementId === agreement.agreementId;
    const isApproving = approvingAgreementId === agreement.agreementId;

    if (agreement.investTokenAddr === ethers.ZeroAddress) {
      // Native BNB investment
      return (
        <div className={styles.actionButtons}>
          <button
            onClick={() => handleInvestBNB(agreement)}
            disabled={isProcessing}
            className="btn btn-primary"
          >
            {isProcessing ? "Investing..." : `Invest ${agreement.investAmountFormatted} BNB`}
          </button>
        </div>
      );
    } else {
      // ERC20 token investment
      const allowanceBigInt = ethers.parseUnits(agreement.allowance || "0", agreement.decimals);
      const needsApproval = allowanceBigInt < agreement.investTokenAmount;

      return (
        <div className={styles.actionButtons}>
          {needsApproval ? (
            <button
              onClick={() => handleApproveERC20(agreement)}
              disabled={isApproving}
              className="btn btn-secondary"
            >
              {isApproving ? "Approving..." : `Approve ${agreement.investTokenSymbol}`}
            </button>
          ) : (
            <button
              onClick={() => handleInvestERC20(agreement)}
              disabled={isProcessing}
              className="btn btn-primary"
            >
              {isProcessing ? "Investing..." : `Invest ${agreement.investAmountFormatted} ${agreement.investTokenSymbol}`}
            </button>
          )}
        </div>
      );
    }
  };

  return (
    <div className={styles.investPage}>
      <TopNavigation fixed />
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>💎 Investment Agreements</h1>
          <p className={styles.subtitle}>Invest in DXF token through your agreements</p>
        </div>

        {!isAuthenticated ? (
          <div className={styles.notSignedIn}>
            <h3>🔒 Sign In Required</h3>
            <p>Connect your wallet to view your investment agreements</p>
          </div>
        ) : isLoading ? (
          <div className={styles.loading}>
            <p>Loading agreements...</p>
          </div>
        ) : agreements.length === 0 ? (
          <div className={styles.noAgreements}>
            <h3>📋 No Agreements Found</h3>
            <p>You don't have any investment agreements yet.</p>
          </div>
        ) : (
          <div className={styles.agreementsGrid}>
            {agreements.map((agreement) => {
              const statusInfo = STATUS_LABELS[agreement.status];
              const isDisabled = agreement.status !== 1 || agreement.isExpired;

              return (
                <div
                  key={agreement.agreementId}
                  className={`${styles.agreementCard} ${isDisabled ? styles.disabled : ""}`}
                >
                  <div className={styles.cardHeader}>
                    <span className={styles.agreementId}>
                      Agreement #{agreement.agreementId}
                    </span>
                    <span className={statusInfo.className}>
                      {agreement.isExpired && agreement.status === 1 ? "Expired" : statusInfo.label}
                    </span>
                  </div>

                  <div className={styles.cardBody}>
                    <div className={styles.infoRow}>
                      <label>Investment Token:</label>
                      <span>{agreement.investTokenSymbol}</span>
                    </div>

                    <div className={styles.infoRow}>
                      <label>Investment Amount:</label>
                      <span className={styles.highlight}>
                        {agreement.investAmountFormatted} {agreement.investTokenSymbol}
                      </span>
                    </div>

                    <div className={styles.infoRow}>
                      <label>DXF Tokens to Receive:</label>
                      <span className={styles.highlightGreen}>
                        {agreement.dxfAmountFormatted} DXF
                      </span>
                    </div>

                    <div className={styles.infoRow}>
                      <label>Expires:</label>
                      <span>{agreement.expiresAtDate.toLocaleString()}</span>
                    </div>

                    {agreement.status === 1 && !agreement.isExpired && agreement.investTokenAddr !== ethers.ZeroAddress && (
                      <>
                        <div className={styles.infoRow}>
                          <label>Your Balance:</label>
                          <span>{parseFloat(agreement.balance || "0").toFixed(4)} {agreement.investTokenSymbol}</span>
                        </div>
                        <div className={styles.infoRow}>
                          <label>Current Allowance:</label>
                          <span>
                            {(() => {
                              try {
                                const allowanceBigInt = ethers.parseUnits(agreement.allowance || "0", agreement.decimals);
                                const maxUint256 = ethers.MaxUint256;

                                // Check if allowance is unlimited (MaxUint256 or very large)
                                if (allowanceBigInt >= maxUint256 / BigInt(2)) {
                                  return <span className={styles.unlimitedAllowance}>Unlimited</span>;
                                }

                                // Format normal allowance
                                const allowanceNum = parseFloat(agreement.allowance || "0");
                                if (allowanceNum === 0) {
                                  return "0";
                                } else if (allowanceNum < 0.0001) {
                                  return allowanceNum.toExponential(2);
                                } else {
                                  return allowanceNum.toFixed(4);
                                }
                              } catch (e) {
                                return agreement.allowance || "0";
                              }
                            })()}{" "}
                            {agreement.allowance && ethers.parseUnits(agreement.allowance, agreement.decimals) < ethers.MaxUint256 / BigInt(2) ? agreement.investTokenSymbol : ""}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  <div className={styles.cardFooter}>
                    {renderAgreementActions(agreement)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AlertPopup alert={alert} onClose={() => {}} />
    </div>
  );
};

export default InvestPage;
